import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, ArrowLeft, RefreshCw, FileSpreadsheet, Printer, Package, Layers } from 'lucide-react';
import Layout from '../../../components/Layout';
import { inventoryReportService, StockValuationSummaryDTO } from '../../../services/inventoryReportService';
import { exportToCsv, printReport } from '../../../utils/exportUtils';
import './InventoryReportCommon.css';

const fmt    = (n: number) => new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const fmtQty = (n: number) => new Intl.NumberFormat().format(n);

const StockValuationReportPage: React.FC = () => {
  const navigate  = useNavigate();
  const [hasSearched, setHasSearched] = useState(false);
  const [searching,   setSearching]   = useState(false);
  const [data,        setData]        = useState<StockValuationSummaryDTO | null>(null);

  const handleLoad = async () => {
    setSearching(true);
    setHasSearched(true);
    try {
      const res = await inventoryReportService.getValuation();
      const d: StockValuationSummaryDTO = (res.data as any)?.data ?? res.data;
      setData(d);
    } catch { setData(null); }
    finally { setSearching(false); }
  };

  const handleExcel = () => {
    if (!data) return;
    exportToCsv(
      `stock-valuation-${new Date().toISOString().split('T')[0]}.csv`,
      ['SKU', 'Product', 'Category', 'Warehouse', 'Quantity', 'Unit Cost', 'Total Value'],
      data.items.map(i => [i.sku, i.productName, i.categoryName ?? '', i.warehouseName ?? '', i.quantity, i.unitCost, i.totalValue]),
    );
  };

  const items = data?.items ?? [];

  return (
    <Layout>
      <div className="irp-page">

        <div className="irp-hero">
          <div>
            <h1 className="irp-hero-title"><DollarSign size={22} strokeWidth={1.8} /> Stock Valuation Report</h1>
            <p className="irp-hero-sub">Total inventory value using last-purchase cost per product</p>
          </div>
          <div className="irp-hero-actions">
            {hasSearched && items.length > 0 && (
              <div className="irp-export-group">
                <button className="irp-export-btn irp-export-excel" onClick={handleExcel}><FileSpreadsheet size={13} /> Export Excel</button>
                <button className="irp-export-btn irp-export-pdf" onClick={printReport}><Printer size={13} /> Print / PDF</button>
              </div>
            )}
            <button className="irp-back-btn" onClick={() => navigate('/reports/inventory')}><ArrowLeft size={14} /> Back</button>
          </div>
        </div>

        <div className="ims-card">
          <div className="ims-card-body" style={{ padding: '1rem 1.25rem' }}>
            <button className="ims-btn ims-btn-primary" onClick={handleLoad} disabled={searching}>
              {searching ? <><span className="irp-spinner" /> Loading…</> : <><RefreshCw size={14} /> Load Valuation</>}
            </button>
          </div>
        </div>

        {!hasSearched && (
          <div className="irp-prompt"><DollarSign size={48} strokeWidth={1} /><p>Click <strong>Load Valuation</strong> to calculate stock value.</p></div>
        )}

        {hasSearched && !searching && data && (
          <>
            <div className="ims-stats-row" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-value"><DollarSign size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value" style={{ fontSize: '1.05rem' }}>{fmt(data.totalValue)}</span>
                  <span className="ims-stat-label">Total Stock Value</span>
                </div>
              </div>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-total"><Package size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value">{data.totalProducts}</span>
                  <span className="ims-stat-label">Products Valued</span>
                </div>
              </div>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-in"><Layers size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value">{fmtQty(data.totalUnits)}</span>
                  <span className="ims-stat-label">Total Units</span>
                </div>
              </div>
            </div>

            {items.length === 0 ? (
              <div className="ims-card"><div className="irp-empty">No stock to value.</div></div>
            ) : (
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">
                    <DollarSign size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                    Inventory Valuation ({items.length} line items)
                  </span>
                </div>
                <div className="ims-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                  <table className="irp-table">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Product</th>
                        <th>Category</th>
                        <th>Warehouse</th>
                        <th className="num">Quantity</th>
                        <th className="num">Unit Cost</th>
                        <th className="num">Total Value</th>
                        <th className="num">% of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => (
                        <tr key={`${item.productId}-${idx}`}>
                          <td><span className="irp-sku">{item.sku}</span></td>
                          <td style={{ fontWeight: 500 }}>{item.productName}</td>
                          <td>{item.categoryName ?? '—'}</td>
                          <td>{item.warehouseName ?? '—'}</td>
                          <td className="num">{fmtQty(item.quantity)}</td>
                          <td className="num">{fmt(item.unitCost)}</td>
                          <td className="num irp-value">{fmt(item.totalValue)}</td>
                          <td className="num" style={{ color: 'var(--ims-text-sub)' }}>
                            {data.totalValue > 0 ? ((item.totalValue / data.totalValue) * 100).toFixed(1) + '%' : '0%'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={4} style={{ fontWeight: 700 }}>TOTAL</td>
                        <td className="num">{fmtQty(data.totalUnits)}</td>
                        <td></td>
                        <td className="num">{fmt(data.totalValue)}</td>
                        <td className="num">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default StockValuationReportPage;
